import React, { useState, useEffect } from "react";
import { useIntl } from "react-intl";

import { faSignInAlt, faEye, faEyeSlash } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";

import Button from "../../UI/Button/Button";
import ErrorPanel, { ErrorSize } from "../../UI/ErrorPanel/ErrorPanel";

import * as UserUtils from "../../Utils/UserUtils";
import * as AuthenticationUtils from "../../Utils/AuthenticationUtils";
import * as DebounceUtils from "../../Utils/DebounceUtils";
import * as ErrorUtils from "../../Utils/ErrorUtils";

import LogService from "../../../middleware/CommonSdk/LogService";
import PropTypes from "prop-types";

const LOG_TAG = "LOGIN_INPUT"
const DURATION_500MS = 500; //ms

const LoginInput = (props) => {
    const [loginEmail, setLoginEmail] = useState("");
    const [password, setPassword] = useState("");
    const [displayClearPassword, setDisplayClearPassword] = useState(false);

    const intl = useIntl();
    const [errorMessage, setErrorMessage] = useState("");
    const [progress, setProgress] = useState(false);
    const [progressEmailChange, setProgressEmailChange] = useState(false);

    useEffect(() => {
        LogService.trace(LOG_TAG, "useEffect loginEmail");
        setLoginEmail(props.loginEmail);
    }, [props.loginEmail]);

    const handleEmailChange = (event) => {
        const email = event.target.value;
        DebounceUtils.clearDebounce();
        setProgressEmailChange(true);

        const action = () => {
            LogService.info(LOG_TAG, "handleEmailChange with:", email);
            setLoginEmail(email);
            if (props.onLoginEmailChange) {
                props.onLoginEmailChange(email)
                    .then(() => {
                        LogService.trace(LOG_TAG, "handleEmailChange success");
                        setErrorMessage("");
                        setProgressEmailChange(false);
                    })
                    .catch((errorPayload) => {
                        LogService.error(LOG_TAG, "handleEmailChange error");
                        const errorMessage = ErrorUtils.getPrettyErrorMessage(errorPayload, intl);
                        setErrorMessage(errorMessage);
                        setProgressEmailChange(false);
                    });
            } else {
                setProgressEmailChange(false);
            }
        };

        if (UserUtils.isValidEmail(email)) {
            DebounceUtils.debounce(action, DURATION_500MS);
        } else if (email.length === 0) {
            DebounceUtils.debounce(action, 0);
        }
    }

    const handlePasswordChange = (event) => {
        const password = event.target.value;
        setPassword(password);
    }

    const handleServerChange = (event) => {
        LogService.trace(LOG_TAG, "handleServerChange");
        if (props.onServerChange) {
            props.onServerChange(loginEmail, parseInt(event.target.value))
                .then(() => {
                    LogService.trace(LOG_TAG, "handleServerChange success");
                    setErrorMessage("");
                })
                .catch((errorPayload) => {
                    LogService.error(LOG_TAG, "handleServerChange error");
                    const errorMessage = ErrorUtils.getPrettyErrorMessage(errorPayload, intl);
                    setErrorMessage(errorMessage);
                });
        }
    }

    const handleAuthenticationChange = (event) => {
        LogService.trace(LOG_TAG, "handleAuthenticationChange");
        const index = parseInt(event.target.value);
        if (props.onAuthenticationChange) {
            props.onAuthenticationChange(index);
        }
    }

    const handleLogin = (event) => {
        LogService.trace(LOG_TAG, "handleLogin");
        event.preventDefault(); //usefull for form submit
        if (props.onLogin) {
            setProgress(true);
            props.onLogin({
                email: loginEmail,
                password: password
            })
                .then(object => {
                    LogService.trace(LOG_TAG, "handleLogin success");
                    setErrorMessage("");
                    if (!object?.keepSpinner) {//modal action
                        setProgress(false);
                    }
                    if (object?.action) {
                        object.action();
                    }
                })
                .catch((object) => {
                    LogService.error(LOG_TAG, "handleLogin error");
                    setProgress(false);
                    const errorMessage = ErrorUtils.getAuthenticationtPrettyErrorMessage(object?.errorPayload, intl);
                    setErrorMessage(errorMessage);
                    if (object?.action) {//modal action
                        object.action();
                    }
                });
        }
    }

    LogService.trace(LOG_TAG, "render");

    let serverSelection = null;
    if (props.server && props.server.length > 1) {
        const serverOptions = props.server.map((item, index) => {
            return (
                <option value={index} key={index}>
                    {item.environmentName}
                </option>
            );
        });

        serverSelection =
            <div className="form-group mt-3">
                <label id="environmentLabel" className="input-label" htmlFor="login_environmentSelect">{intl.messages.loginServerTitle}</label>
                <select id="environmentSelect" className="input-group form-control"
                    value={props.selectedServerIndex}
                    onChange={handleServerChange}>
                    {serverOptions}
                </select>
            </div>
    }

    let authenticationSelection = null;
    if (props.authentication && props.authentication.length > 1) {
        const authenticationOptions = props.authentication.map((item, index) => {
            let displayName = item.type;
            if (item.type === AuthenticationUtils.AUTHENTICATION_TYPES.SAML) {
                displayName = intl.messages.loginAuthenticationSamlTitle;
            }
            return (
                <option value={index} key={index}>
                    {displayName}
                </option>
            );
        });

        authenticationSelection =
            <div className="form-group mt-3">
                <label id="authenticationLabel" className="input-label" htmlFor="login_authenticationSelect">{intl.messages.loginAuthenticationTitle}</label>
                <select id="authenticationSelect" className="input-group form-control"
                    value={props.selectedAuthenticationIndex}
                    onChange={handleAuthenticationChange}>
                    {authenticationOptions}
                </select>
            </div>
    }

    const isSso = AuthenticationUtils.isSsoAuthentication({
        environment: props.server,
        authentication: props.authentication,
        selectedLoginIndex: props.selectedAuthenticationIndex
    });

    const disableLoginButton = progressEmailChange || (!isSso && password.length === 0) || !props.enableLoginButton;
    const disablePassword = progressEmailChange || isSso;


    return (
        <div>
            <form onSubmit={(event) => event.preventDefault()}>
                <label id="emailLabel" className="input-label" htmlFor="email">{intl.messages.loginEmail}</label>
                <div className="input-group mb-3">
                    <input
                        id="email"
                        type="email"
                        className="form-control"
                        placeholder={intl.messages.loginEmailPlaceHolder}
                        autoComplete="true"
                        onChange={handleEmailChange}
                    />
                </div>

                <label id="passwordLabel" className="input-label" htmlFor="password">{intl.messages.loginPwd}</label>
                <div className="input-group">
                    <input
                        id="password"
                        type={displayClearPassword ? "text" : "password"}
                        className="form-control"
                        placeholder={intl.messages.loginPwdPlaceHolder}
                        autoComplete="true"
                        onChange={handlePasswordChange}
                        disabled={disablePassword}
                    />
                    <div className="input-group-append">
                        <button className="btn input-btn" type="button" onClick={() => setDisplayClearPassword(!displayClearPassword)}>
                            <FontAwesomeIcon icon={displayClearPassword ? faEyeSlash : faEye} />
                        </button>
                    </div>
                </div>
                {!isSso &&
                    <small id="resetPassword" className="input-group"><a href={props.forgotPasswordUrl} className="text-green">{intl.messages.loginForgotPWD}</a></small>
                }

                {serverSelection}
                {authenticationSelection}

                <div className="mt-3">
                    <Button
                        type="submit"
                        className="btn-block btn-submit"
                        label={isSso ? intl.messages.loginSsoButton : intl.messages.loginButton}
                        icon={faSignInAlt}
                        onClick={handleLogin}
                        spinner={progress}
                        disabled={disableLoginButton}
                    />
                    {(errorMessage) &&
                        <div>
                            <ErrorPanel size={ErrorSize.Small} >{errorMessage}</ErrorPanel>
                        </div>
                    }
                </div>
            </form>
        </div>
    );
}

LoginInput.defaultProps = {
    loginEmail: "",
    selectedServerIndex: 0,
    selectedAuthenticationIndex: 0,
    enableLoginButton: false,
}

LoginInput.propTypes = {
    loginEmail: PropTypes.string,
    server: PropTypes.array,
    authentication: PropTypes.array,
    selectedServerIndex: PropTypes.number,
    selectedAuthenticationIndex: PropTypes.number,
    enableLoginButton: PropTypes.bool,
    onLoginEmailChange: PropTypes.func,
    onServerChange: PropTypes.func,
    onAuthenticationChange: PropTypes.func,
    onLogin: PropTypes.func,
    forgotPasswordUrl: PropTypes.object,
};

export default LoginInput;